import { qs } from "./core";
import Url from "./url";

export function replaceBase(doc: Document, section: { url: string }): void {
	let base;
	let url = section.url;
	const absolute = url.includes("://");

	if(!doc){
		return;
	}

	const head = qs(doc, "head");
	base = qs(head!, "base");

	if(!base) {
		base = doc.createElement("base");
		head!.insertBefore(base, head!.firstChild);
	}

	// Fix for Safari crashing if the url doesn't have an origin
	if (!absolute && typeof window !== "undefined" && window.location) {
		url = window.location.origin + url;
	}

	base.setAttribute("href", url);
}

export function replaceCanonical(doc: Document, section: { canonical: string }): void {
	let link;
	const url = section.canonical;

	if(!doc){
		return;
	}

	const head = qs(doc, "head");
	link = qs(head!, "link[rel='canonical']");

	if (link) {
		link.setAttribute("href", url);
	} else {
		link = doc.createElement("link");
		link.setAttribute("rel", "canonical");
		link.setAttribute("href", url);
		head!.appendChild(link);
	}
}

export function replaceMeta(doc: Document, section: { idref: string }): void {
	let meta;
	const id = section.idref;
	if(!doc){
		return;
	}

	const head = qs(doc, "head");
	meta = qs(head!, "link[property='dc.identifier']");

	if (meta) {
		meta.setAttribute("content", id);
	} else {
		meta = doc.createElement("meta");
		meta.setAttribute("name", "dc.identifier");
		meta.setAttribute("content", id);
		head!.appendChild(meta);
	}
}

export function replaceLinks(contents: Element, fn: (path: string) => void): void {

	const links = contents.querySelectorAll("a[href]");

	if (!links.length) {
		return;
	}

	const base = qs(contents.ownerDocument, "base");
	const location = base ? base.getAttribute("href") ?? undefined : undefined;
	const replaceLink = function(link: Element): void {
		const href = link.getAttribute("href") ?? "";

		if(href.startsWith("mailto:")){
			return;
		}

		// The URL parser strips tab/newline from anywhere in the input and trims
		// surrounding whitespace, so "java\tscript:" navigates as "javascript:". Strip a
		// superset of those before sniffing, or we check a string the browser won't use.
		// eslint-disable-next-line no-control-regex
		const hrefNormalized = href.replace(/[\u0000-\u0020]+/g, "").toLowerCase();
		if(hrefNormalized.startsWith("javascript:") || hrefNormalized.startsWith("data:text/html") || hrefNormalized.startsWith("vbscript:")){
			link.removeAttribute("href");
			return;
		}

		const absolute = href.includes("://");

		if(absolute){

			link.setAttribute("target", "_blank");

		}else{
			let linkUrl: Url | undefined;
			try {
				linkUrl = new Url(href, location);
			} catch(_error) {
				// NOOP
			}

			(link as HTMLElement).onclick = function(): boolean {

				if(linkUrl && linkUrl.hash) {
					fn(linkUrl.Path.path + linkUrl.hash);
				} else if(linkUrl){
					fn(linkUrl.Path.path);
				} else {
					fn(href);
				}

				return false;
			};
		}
	};

	for (let i = 0; i < links.length; i++) {
		replaceLink(links[i]!);
	}


}

export function substitute(content: string, urls: string[], replacements: (string | null)[]): string {
	const map = new Map<string, string>();
	const alternatives: string[] = [];
	const escapeRe = /[-[\]{}()*+?.,\\^$|#\s]/g;

	for (let i = 0; i < urls.length; i++) {
		const url = urls[i];
		const replacement = replacements[i];
		if (!url || !replacement) continue;

		const escapedUrl = url.replace(escapeRe, "\\$&");
		map.set(url, replacement);
		alternatives.push(escapedUrl);

		try {
			const decoded = decodeURIComponent(url);
			if (decoded !== url) {
				const escapedDecoded = decoded.replace(escapeRe, "\\$&");
				map.set(decoded, replacement);
				alternatives.push(escapedDecoded);
			}
		} catch (_e) {
			// Invalid URI encoding, skip decoded variant
		}
	}

	if (alternatives.length === 0) return content;

	// Sort longest first to prevent substring collisions
	alternatives.sort((a, b) => b.length - a.length);
	const combined = new RegExp(alternatives.join("|"), "g");
	return content.replace(combined, (match) => map.get(match) ?? match);
}
